import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Reveal from '../components/Reveal';
import { loadPublications } from '../content/publications';

const BLOG_POSTS = [];

const RESOURCE_TRACKS = [
  {
    title: 'Clinical operations playbooks',
    items: ['Deviation management templates', 'Sponsor-ready reporting cadence', 'Site activation checklist'],
  },
  {
    title: 'Product releases',
    items: ['CTMS workflow enhancements', 'AI assistance updates', 'Compliance reporting toolkit'],
  },
  {
    title: 'Events & webinars',
    items: ['Operational excellence roundtable', 'Part 11 readiness workshop', 'Sponsor QA summit'],
  },
];

const DEFAULT_PUBLICATION_IMAGE = '/logo-full-transparent.png';

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BLOG_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://phennx.io/blog#webpage',
      url: 'https://phennx.io/blog',
      name: 'News & Updates - Phennx',
      description: 'Stay up to date with the latest news, product updates, and insights from Phennx.',
      isPartOf: { '@id': 'https://phennx.io/#website' },
      about: { '@id': 'https://phennx.io/#organization' },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://phennx.io/blog#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://phennx.io/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'News',
          item: 'https://phennx.io/blog',
        },
      ],
    },
  ],
};

export default function BlogPage() {
  const [publications, setPublications] = useState([]);
  const [loadState, setLoadState] = useState('loading');

  useEffect(() => {
    let isActive = true;

    loadPublications()
      .then((items) => {
        if (!isActive) {
          return;
        }
        const sortedItems = items
          .filter((item) => !item.draft)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setPublications(sortedItems);
        setLoadState('ready');
      })
      .catch(() => {
        if (isActive) {
          setLoadState('error');
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  const featuredPublications = useMemo(
    () => publications.filter((item) => item.featured),
    [publications],
  );

  // Combine sample blog posts with featured publications
  const allPosts = useMemo(() => {
    const publicationPosts = featuredPublications.map((pub) => ({
      id: `pub-${pub.slug}`,
      slug: pub.slug,
      title: pub.title,
      excerpt: pub.summary || pub?.seo?.description || '',
      author: pub.author || 'PHENNX.IO',
      date: pub.date,
      category: pub.category || 'Publication',
      image: pub.heroImage || DEFAULT_PUBLICATION_IMAGE,
      readTime: pub.readTime || '5 min read',
      isPublication: true, // Flag to use /publications route
    }));

    return [...publicationPosts, ...BLOG_POSTS].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [featuredPublications]);

  return (
    <>
      <SEO
        title="News & Updates - Phennx"
        description="Stay up to date with the latest news, product updates, and insights from Phennx."
        path="/blog"
        image="/logo-full-transparent.png"
        type="website"
        schema={BLOG_SCHEMA}
      />
      <article className="blog-page" aria-label="News & Updates">
        <header className="blog-page__hero">
          <div className="section-inner">
            <div className="blog-page__eyebrow">News</div>
            <h1 className="blog-page__title">Latest Updates & Insights</h1>
            <p className="blog-page__subtitle">
              Stay informed about product updates, industry insights, and best practices in clinical research.
            </p>
          </div>
        </header>

        <section className="blog-page__content">
          <div className="section-inner">
            {loadState === 'loading' && (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <p style={{ color: 'var(--text-muted)' }}>Loading news...</p>
              </div>
            )}
            {loadState === 'error' && (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <p style={{ color: 'var(--text-muted)' }}>Unable to load news. Please try again later.</p>
              </div>
            )}
            {loadState === 'ready' && (
              <Reveal>
                <div className="blog-grid">
                  {allPosts.map((post) => (
                    <article key={post.id} className="blog-card">
                      <Link
                        to={post.isPublication ? `/publications/${post.slug}` : `/blog/${post.slug}`}
                        className="blog-card__link"
                      >
                        <div className="blog-card__image-wrapper">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="blog-card__image"
                            loading="lazy"
                          />
                          <div className="blog-card__overlay" />
                        </div>
                        <div className="blog-card__content">
                          <div className="blog-card__category">{post.category}</div>
                          <h2 className="blog-card__title">{post.title}</h2>
                          <p className="blog-card__excerpt">{post.excerpt}</p>
                          <div className="blog-card__meta">
                            <span className="blog-card__author">{post.author}</span>
                            <span className="blog-card__separator">•</span>
                            <time className="blog-card__date" dateTime={post.date}>
                              {formatDate(post.date)}
                            </time>
                            <span className="blog-card__separator">•</span>
                            <span className="blog-card__read-time">{post.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </Reveal>
            )}
          </div>
        </section>

        <section className="section blog-page__resources">
          <div className="section-inner blog-page__resources-inner">
            <div className="blog-page__resources-copy">
              <div className="blog-page__eyebrow">Resource tracks</div>
              <h2 className="section-title">Find the guidance your teams need most.</h2>
              <p className="section-subtitle">
                Browse curated tracks and connect with the Phennx team for tailored operational recommendations.
              </p>
              <Link className="btn-primary blog-page__resources-cta" to="/#contact">
                Talk with our team
              </Link>
            </div>
            <div className="blog-page__resources-list" role="list">
              {RESOURCE_TRACKS.map((track) => (
                <div key={track.title} className="blog-page__track" role="listitem">
                  <h3>{track.title}</h3>
                  <ul>
                    {track.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </article>
    </>
  );
}

